import React, { useEffect, useState, useCallback } from 'react';
import { Server, RefreshCw, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Phase A++ — Riya Proxy Health widget.
 *
 * Surfaces /api/admin/riya/proxy-health?shots=10 in real time so ops can
 * spot Bright Data residential-pool degradation BEFORE bookings start
 * failing. Auto-refreshes every 60 s.
 *
 * Verdict mapping:
 *   • healthy   ≥90% success — green
 *   • degraded  50-89% success — amber
 *   • down      <50% success — red
 */
const RiyaProxyHealthCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(
        `${BACKEND_URL}/api/admin/riya/proxy-health?shots=10`,
        { withCredentials: true, timeout: 35000 },
      );
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Probe failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const t = setInterval(fetchHealth, 60 * 1000); // 60 s auto-refresh
    return () => clearInterval(t);
  }, [fetchHealth]);

  const theme = (() => {
    const v = data?.verdict;
    if (v === 'down') {
      return {
        bg: 'bg-red-50', border: 'border-red-200',
        text: 'text-red-700', sub: 'text-red-600',
        icon: XCircle, label: 'Down',
        pillBg: 'bg-red-500',
      };
    }
    if (v === 'degraded') {
      return {
        bg: 'bg-amber-50', border: 'border-amber-200',
        text: 'text-amber-700', sub: 'text-amber-600',
        icon: AlertTriangle, label: 'Degraded',
        pillBg: 'bg-amber-500',
      };
    }
    return {
      bg: 'bg-emerald-50', border: 'border-emerald-200',
      text: 'text-emerald-700', sub: 'text-emerald-600',
      icon: CheckCircle, label: 'Healthy',
      pillBg: 'bg-emerald-500',
    };
  })();

  const Icon = theme.icon;
  const successPct = data ? Math.round((data.success_rate || 0) * 100) : 0;

  return (
    <div
      className={`rounded-xl border ${theme.border} ${theme.bg} p-5 transition-shadow hover:shadow-md`}
      data-testid="riya-proxy-health-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Server className={`w-5 h-5 ${theme.text}`} />
          <h3 className="text-sm font-bold text-gray-800">
            Riya Proxy Health
          </h3>
        </div>
        <button
          onClick={fetchHealth}
          disabled={loading}
          data-testid="riya-proxy-health-refresh"
          className="text-xs text-gray-500 hover:text-gray-800 disabled:opacity-50 flex items-center gap-1"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Probing…' : 'Probe'}
        </button>
      </div>

      {error && (
        <div className="text-xs text-red-700 bg-red-100 rounded p-2 mb-2" data-testid="riya-proxy-health-error">
          {typeof error === 'string' ? error : 'Probe error'}
        </div>
      )}

      {!data && !error && (
        <div className="text-xs text-gray-500">Firing 10 concurrent probes…</div>
      )}

      {data && (
        <>
          <div className="flex items-end justify-between mb-3">
            <div className="flex items-center gap-2">
              <Icon className={`w-5 h-5 ${theme.sub}`} />
              <span
                className={`text-xl font-bold ${theme.text}`}
                data-testid="riya-proxy-health-verdict"
              >
                {theme.label}
              </span>
            </div>
            <div className="text-right">
              <div className={`text-2xl font-bold ${theme.text}`} data-testid="riya-proxy-health-rate">
                {successPct}%
              </div>
              <div className="text-[10px] text-gray-500 -mt-1">
                {data.success}/{data.shots} OK
              </div>
            </div>
          </div>

          {/* Per-shot pill row */}
          <div className="flex items-center gap-1 mb-3" data-testid="riya-proxy-health-pills">
            {(data.results || []).map((r) => (
              <div
                key={r.attempt}
                title={`#${r.attempt}: ${r.ok ? 'OK' : `FAIL (${r.http || 'EXC'})`} · ${r.latency_ms}ms${r.err ? ` · ${r.err}` : ''}`}
                className={`flex-1 h-2 rounded-full ${
                  r.ok ? 'bg-emerald-500' : (r.http && r.http >= 500 ? 'bg-red-500' : 'bg-amber-500')
                }`}
              />
            ))}
          </div>

          {/* Latency stats */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">p50 latency</p>
              <p className="font-semibold text-gray-800">{data.latency_p50_ms ?? '–'} ms</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">p95 latency</p>
              <p className="font-semibold text-gray-800">{data.latency_p95_ms ?? '–'} ms</p>
            </div>
          </div>

          <div className="text-[10px] text-gray-400 mt-3 break-all">
            {data.proxy_url || 'no proxy'} · auto-probe every 60 s
          </div>
        </>
      )}
    </div>
  );
};

export default RiyaProxyHealthCard;
